import { Grid, Paper, Box, Divider, Button, Tooltip } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import MainContentHeader from 'src/shared/components/atoms/mainContentHeader';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import invoiceMissionService, {
  createAutogiroFile,
  sendAutogiroFile,
  undoCreateAutogiroInvoices,
  getAutogiroFileStatus,
} from 'src/shared/services/invoiceMissionService';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { InvoiceMissionResponse } from 'src/shared/models/invoiceMission/invoiceMission';
import { ContentSection } from 'src/shared/components/organisms/contentSection';
import { InvoiceMissionTypeTextEnum } from 'src/shared/models/enums/invoiceMissionTypeText.enum';
import { useLocalStorage } from 'src/shared/hooks/useLocalStorage';
import { LocalStorageKeys } from 'src/shared/models/enums/localStorageKeys.enum';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import { InvoiceSendingTypeIntegerEnum } from 'src/shared/models/enums/invoiceSendingTypeInteger.enum';
import { DeleteConfirmationDialog } from 'src/shared/components/organisms/deleteConfirmationDialog';
import { toast } from 'react-toastify';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { FSVersionEnum } from 'src/shared/models/enums/FSVersionEnum';
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE } from 'src/shared/models/enums/gridFilterConfig';
import { getPermission } from 'src/shared/functions/getSysProductPermissions';
import { PermissionEnum } from 'src/shared/models/enums/permissionEnums';
import { useGlobals } from 'src/shared/contexts/appContext';
import InvoiceDataGrid from '../components/invoiceDataGrid';
import { CreateInvoiceMissionRefundModal } from '../components/createInvoiceMissionRefundModal';
import { InvoiceSearchFilter } from '../models/invoiceSearchFilter';
import { SendingSection } from '../components/sendingSection';
import { SendingSessionSection } from '../components/sendingSessionSection';
import InvoiceTabs, { invoiceTabs } from '../components/invoiceTabs';
import { InvoiceMissionDetailTabs } from '../components/invoiceMissionDetailTabs';
import { convertSendingTypeIntToString } from '../components/invoiceMissionSendingTypeConverter';

// Aviseringar (uppdrag) detaljvy
export const InvoiceMissionDetailsPage = () => {
  const { t } = useTranslation();
  const params = useParams<{ id: string; sendingType: string }>();
  const navigate = useNavigate();
  const { currentUser } = useGlobals();
  const [isFSVersion2, setIsFSVersion2] = useState<boolean>(true);
  const isSuperAdmin = getPermission(currentUser, PermissionEnum.InvoiceCreateForCustomer);
  // Radering (avisering + påminnelse) kräver INVOICE_DELETE (Privilegierad/Administratör/SuperAdmin på Fakturering).
  const canDelete = getPermission(currentUser, PermissionEnum.InvoiceDelete);

  const [loading, setIsLoading] = useState(false);
  const [refetchDataGrid, setRefetchDataGrid] = useState(false);
  const [invoiceMission, setInvoiceMission] = useState<InvoiceMissionResponse>();
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [openDeleteSendingDialog, setOpenDeleteSendingDialog] = useState<boolean>(false);
  const [openUndoAutogiroDialog, setOpenUndoAutogiroDialog] = useState<boolean>(false);
  const [autogiroFileSent, setAutogiroFileSent] = useState<boolean>(false);
  const [autogiroFileExists, setAutogiroFileExists] = useState<boolean>(false);
  const [autogiroLoading, setAutogiroLoading] = useState(false);
  const [openRefundModal, setOpenRefundModal] = useState(false);

  const isAutogiro = invoiceMission?.type === InvoiceMissionTypeTextEnum.autogiro;

  // Aktuell påminnelseflik (3 = Påminnelse 1, 4 = Påminnelse 2) – knappen "Ta bort påminnelse" visas bara där.
  const sendingTypeInt = params.sendingType ? Number(params.sendingType) : InvoiceSendingTypeIntegerEnum.NotSet;
  const isReminderTab = sendingTypeInt === InvoiceSendingTypeIntegerEnum.Reminder1 || sendingTypeInt === InvoiceSendingTypeIntegerEnum.Reminder2;
  const reminderNumber = sendingTypeInt === InvoiceSendingTypeIntegerEnum.Reminder2 ? 2 : 1;
  // Utskickets id (invoiceMissionSending_id) för aktuell flik. 0/undefined = ingen session skapad = inget att ta bort.
  const reminderSendingId = isReminderTab
    ? invoiceMission?.dataList?.find((d) => d.sendingType === convertSendingTypeIntToString(sendingTypeInt))?.approval?.id
    : undefined;

  const [invoiceMissionDetailFilter, setInvoiceMissionDetailFilter] = useLocalStorage(LocalStorageKeys.InvoiceMissionDetailFilter, {
    status: invoiceTabs.filter((x) => x.key === 'All')[0],
  } as unknown as InvoiceSearchFilter);
  const isMounted = useIsMounted();
  const lastFetchedSendingType = useRef<string | undefined>(params.sendingType);

  const searchFilter: InvoiceSearchFilter = useMemo(() => {
    return {
      ownerId: undefined,
      invoiceMissionId: invoiceMission?.invoiceMission_id,
      searchString: '',
      status: invoiceMissionDetailFilter?.status,
      isSent: invoiceMissionDetailFilter?.isSent,
      sendingType: params.sendingType ? Number(params.sendingType) : InvoiceSendingTypeIntegerEnum.NotSet,
      pageIndex: invoiceMissionDetailFilter?.pageIndex ?? DEFAULT_PAGE_INDEX,
      pageSize: invoiceMissionDetailFilter?.pageSize ?? DEFAULT_PAGE_SIZE,
    } as unknown as InvoiceSearchFilter;
  }, [
    invoiceMission?.invoiceMission_id,
    invoiceMissionDetailFilter?.isSent,
    invoiceMissionDetailFilter?.pageIndex,
    invoiceMissionDetailFilter?.pageSize,
    invoiceMissionDetailFilter?.status,
    params.sendingType,
  ]);

  useEffect(() => {
    if (searchFilter.invoiceMissionId?.toString() !== params.id) {
      setInvoiceMissionDetailFilter((prev) => ({
        ...prev,
        sendingType: 0,
      }));
    }
  }, [params.id, searchFilter.invoiceMissionId, setInvoiceMissionDetailFilter]);

  const fetchInvoiceMission = useCallback(async () => {
    setIsLoading(true);

    try {
      if (isMounted() && params.id) {
        const result = await invoiceMissionService.getById(params.id, searchFilter.sendingType, isSuperAdmin ? true : undefined);
        setInvoiceMission(result.data);
        lastFetchedSendingType.current = params.sendingType;
        if (result.data.source !== FSVersionEnum.FS20) {
          setIsFSVersion2(false);
        }
      }
    } catch (err) {
      if (isMounted()) ConsoleHelper.log(err);
    } finally {
      if (isMounted()) setIsLoading(false);
    }
  }, [isMounted, isSuperAdmin, params.id, params.sendingType, searchFilter.sendingType]);

  useEffect(() => {
    fetchInvoiceMission();
  }, [fetchInvoiceMission]);

  // Hämta om autogirofilen redan skickats till Bankgirot, så vi kan varna innan man ångrar.
  const fetchAutogiroFileStatus = useCallback(
    async (missionId: number) => {
      try {
        const res = await getAutogiroFileStatus(missionId);
        if (isMounted()) {
          setAutogiroFileSent(res.data.sent);
          setAutogiroFileExists(res.data.hasFile);
        }
      } catch (e) {
        if (isMounted()) ConsoleHelper.log(e);
      }
    },
    [isMounted],
  );

  useEffect(() => {
    if (isAutogiro && invoiceMission?.invoiceMission_id) {
      fetchAutogiroFileStatus(invoiceMission.invoiceMission_id);
    }
  }, [isAutogiro, invoiceMission?.invoiceMission_id, fetchAutogiroFileStatus]);

  const handleCreateAutogiroFile = async () => {
    if (!invoiceMission) return;
    setAutogiroLoading(true);
    toast.info(t('invoice:invoiceMissionPage.autogiroFileProcessing') as string);
    try {
      await createAutogiroFile(invoiceMission.invoiceMission_id);
      toast.success(t('invoice:invoiceMissionPage.createAutogiroFileSuccess') as string);
      fetchInvoiceMission();
      fetchAutogiroFileStatus(invoiceMission.invoiceMission_id);
    } catch (e) {
      ConsoleHelper.log(e);
      toast.error(t('invoice:invoiceMissionPage.createAutogiroFileError') as string);
    } finally {
      setAutogiroLoading(false);
    }
  };

  const handleSendAutogiroFile = async () => {
    if (!invoiceMission) return;
    setAutogiroLoading(true);
    toast.info(t('invoice:invoiceMissionPage.autogiroFileProcessing') as string);
    try {
      await sendAutogiroFile(invoiceMission.invoiceMission_id);
      toast.success(t('invoice:invoiceMissionPage.sendAutogiroFileSuccess') as string);
      fetchInvoiceMission();
      fetchAutogiroFileStatus(invoiceMission.invoiceMission_id);
    } catch (e) {
      ConsoleHelper.log(e);
      toast.error(t('invoice:invoiceMissionPage.sendAutogiroFileError') as string);
    } finally {
      setAutogiroLoading(false);
    }
  };

  const onHandleUndoAutogiro = async (id: number) => {
    try {
      await undoCreateAutogiroInvoices(id);
      if (isMounted()) {
        toast.success(t('invoice:invoiceMissionPage.undoAutogiroSuccess') as string);
        navigate(`${RoutesEnum.InvoiceMission}`);
      }
    } catch (e) {
      ConsoleHelper.log(e);
      toast.error(t('invoice:invoiceMissionPage.undoAutogiroError') as string);
    } finally {
      if (isMounted()) setOpenUndoAutogiroDialog(false);
    }
  };

  const loadingCallback = useCallback(
    (isLoading: boolean) => {
      if (isMounted()) {
        ConsoleHelper.log('loadingCallback InvoiceMissionDetailsPage datagrid', isLoading);
        setIsLoading(false);
      }
    },
    [isMounted],
  );

  const onHandleDelete = async (id: number) => {
    try {
      await invoiceMissionService.deleteInvoiceMission(id);
      if (isMounted()) {
        toast.success(<>{t('invoice:invoiceMissionPage.deleteInvoiceMissionSuccess')}</>);
        navigate(`${RoutesEnum.InvoiceMission}`);
      }
    } catch (e) {
      toast.error(<>{t('invoice:invoiceMissionPage.deleteInvoiceMissionError')}</>);
    } finally {
      if (isMounted()) {
        setOpenDeleteDialog(false);
      }
    }
  };

  // Tar bort ett påminnelseutskick (påminnelse 1/2). Efteråt: gå till Alla-fliken (påminnelsens data är borta).
  const onHandleDeleteSending = async (id: number) => {
    try {
      await invoiceMissionService.deleteInvoiceMissionSending(id);
      if (isMounted()) {
        toast.success(t('invoice:invoiceMissionPage.deleteReminderSendingSuccess') as string);
        navigate(`${RoutesEnum.InvoiceMission}/${params.id}/0`);
      }
    } catch (e) {
      toast.error(t('invoice:invoiceMissionPage.deleteReminderSendingError') as string);
    } finally {
      if (isMounted()) {
        setOpenDeleteSendingDialog(false);
      }
    }
  };

  const handleRefetch = useCallback(
    (refetch: boolean) => {
      if (refetch) {
        fetchInvoiceMission();
        setRefetchDataGrid(true);
      }
    },
    [fetchInvoiceMission],
  );

  return (
    <Grid container>
      <LoadingOverlay loading={loading} />
      <Grid container justifyContent='space-between' mb={2.5}>
        <Grid item xs='auto' sx={{ display: 'flex', alignItems: 'center' }}>
          <MainContentHeader title={`${invoiceMission?.missionName}`} isLoading={loading} />
        </Grid>
        {invoiceMission && (
          <Grid item xs='auto' sx={{ marginTop: { xs: 1, sm: 0 }, display: 'flex', gap: 1 }}>
            {isSuperAdmin && isAutogiro && (
              <>
                <Button size='medium' variant='outlined' onClick={() => setOpenRefundModal(true)}>
                  {t('invoice:refund.openButton')}
                </Button>
                <Tooltip title={autogiroFileExists ? t('invoice:invoiceMissionPage.createAutogiroFileDisabledTooltip') : ''}>
                  <span>
                    <Button size='medium' variant='outlined' disabled={autogiroLoading || autogiroFileExists} onClick={handleCreateAutogiroFile}>
                      {t('invoice:invoiceMissionPage.createAutogiroFile')}
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip title={autogiroFileSent ? t('invoice:invoiceMissionPage.sendAutogiroFileDisabledTooltip') : ''}>
                  <span>
                    <Button size='medium' variant='outlined' disabled={autogiroLoading || autogiroFileSent} onClick={handleSendAutogiroFile}>
                      {t('invoice:invoiceMissionPage.sendAutogiroFile')}
                    </Button>
                  </span>
                </Tooltip>
                <Button
                  size='medium'
                  variant='outlined'
                  color='error'
                  disableElevation
                  disabled={autogiroLoading}
                  onClick={() => setOpenUndoAutogiroDialog(true)}
                  type='button'
                >
                  {t('invoice:invoiceMissionPage.undoAutogiro')}
                </Button>
              </>
            )}
            {canDelete && isFSVersion2 && !isAutogiro && (
              <Button size='medium' onClick={() => setOpenDeleteDialog(true)} disableElevation variant='outlined' color='error' type='button'>
                {t('invoice:invoiceMissionPage.deleteInvoiceMission')}
              </Button>
            )}
          </Grid>
        )}
      </Grid>
      <Grid item xs={12}>
        <Paper sx={{ px: 3, overflow: 'hidden' }}>
          {invoiceMission && (
            <GridWrapper>
              <Grid item xs={12} ml={-3}>
                <InvoiceMissionDetailTabs dataList={invoiceMission.dataList} />
              </Grid>

              <Grid item xs={12} lg={invoiceMission.type === InvoiceMissionTypeTextEnum.member ? 6 : 12}>
                <ContentSection header={t('invoice:invoiceMissionPage.mission')}>
                  <SendingSection
                    key={params.sendingType}
                    invoiceMission={invoiceMission}
                    loading={loading || lastFetchedSendingType.current !== params.sendingType}
                  />
                </ContentSection>
              </Grid>

              {invoiceMission.type === InvoiceMissionTypeTextEnum.member && (
                <Grid item xs={12} lg={6}>
                  <ContentSection header={t('invoice:invoiceMissionPage.sending')}>
                    <SendingSessionSection
                      key={params.sendingType}
                      invoiceMission={invoiceMission}
                      loading={loading || lastFetchedSendingType.current !== params.sendingType}
                      isFSVersion2={isFSVersion2}
                      refetchMission={handleRefetch}
                      onDeleteReminderSending={canDelete ? () => setOpenDeleteSendingDialog(true) : undefined}
                    />
                  </ContentSection>
                </Grid>
              )}
              <Grid item xs={12}>
                <Box sx={{ mx: -3 }}>
                  <InvoiceTabs
                    initialValue={searchFilter.status || undefined}
                    disableAll={loading}
                    callback={(val) => {
                      setInvoiceMissionDetailFilter((prev) => ({ ...prev, ...{ status: val, isSent: val.isSent, pageIndex: 0 } }));
                    }}
                    includeSentStatusTabs
                  />
                </Box>
                <Divider sx={{ mx: -3 }} />
                <Box className='section-inset' sx={{ mx: -3 }}>
                  <InvoiceDataGrid
                    loadingCallback={loadingCallback}
                    searchFilter={searchFilter}
                    searchFilterCallback={(newFilter) => {
                      setInvoiceMissionDetailFilter((prev) => ({
                        ...prev,
                        ...newFilter,
                      }));
                    }}
                    hideCheckBoxSelection
                    refetchData={refetchDataGrid}
                    setRefetchData={(val: boolean) => setRefetchDataGrid(val)}
                  />
                </Box>
              </Grid>
            </GridWrapper>
          )}
        </Paper>
      </Grid>
      {invoiceMission && (
        <DeleteConfirmationDialog
          title={t('common:confirmDelete')}
          value={invoiceMission.invoiceMission_id.toString()}
          modaltext={t('common:irreversibleAction')}
          onClose={(value) => {
            if (value) {
              const id = Number(value.id);
              onHandleDelete(id);
            } else {
              setOpenDeleteDialog(false);
            }
          }}
          open={openDeleteDialog}
        />
      )}
      {invoiceMission && !!reminderSendingId && (
        <DeleteConfirmationDialog
          title={t('invoice:invoiceMissionPage.deleteReminderSendingConfirmTitle', { number: reminderNumber })}
          value={reminderSendingId.toString()}
          modaltext={t('invoice:invoiceMissionPage.deleteReminderSendingConfirmText', { number: reminderNumber })}
          confirmButtonText={t('invoice:invoiceMissionPage.deleteReminderSendingConfirmButton')}
          confirmButtonColor='error'
          onClose={(value) => {
            if (value) {
              onHandleDeleteSending(Number(value.id));
            } else {
              setOpenDeleteSendingDialog(false);
            }
          }}
          open={openDeleteSendingDialog}
        />
      )}
      {invoiceMission && (
        <DeleteConfirmationDialog
          title={t('invoice:invoiceMissionPage.undoAutogiroConfirmTitle')}
          value={invoiceMission.invoiceMission_id.toString()}
          modaltext={
            autogiroFileSent ? t('invoice:invoiceMissionPage.undoAutogiroSentWarning') : t('invoice:invoiceMissionPage.undoAutogiroConfirmText')
          }
          confirmButtonText={t('invoice:invoiceMissionPage.undoAutogiroConfirmButton')}
          confirmButtonColor='error'
          onClose={(value) => {
            if (value) {
              onHandleUndoAutogiro(Number(value.id));
            } else {
              setOpenUndoAutogiroDialog(false);
            }
          }}
          open={openUndoAutogiroDialog}
        />
      )}
      {invoiceMission && openRefundModal && (
        <CreateInvoiceMissionRefundModal
          open={openRefundModal}
          missionId={invoiceMission.invoiceMission_id}
          onClose={() => setOpenRefundModal(false)}
          onCreated={(newMissionId) => {
            setOpenRefundModal(false);
            navigate(`${RoutesEnum.InvoiceMission}/${newMissionId}/0`);
          }}
        />
      )}
    </Grid>
  );
};
